import uid from "common/utils/uid";
import type { DocMigration } from "../docMigration";

interface MigrationDocJson {
  elevations?: Record<string, unknown>;
  entityRefs?: Record<string, unknown>;
  optionSets?: Record<string, { defaultValue?: string } | undefined>;
}

const migration: DocMigration = {
  id: "20260901_seedlocalelevationwhenalldocrefsourced",
  description: "Seed a local default elevation for docs where every elevation is doc-ref sourced",
  apply(json: any): any {
    const doc = json as MigrationDocJson;
    const elevationIds = Object.keys(doc.elevations ?? {});

    if (elevationIds.length === 0) {
      return json;
    }

    const entityRefs = doc.entityRefs ?? {};
    const allDocRefSourced = elevationIds.every((id) => !!entityRefs[id]);

    if (!allDocRefSourced) {
      return json;
    }

    /**
     * A doc with every elevation doc-ref sourced could have all of them removed by a future
     * doc-ref update, leaving `doc.elevations` empty and crashing anything that resolves an
     * active elevation. New docs can no longer reach this state -- `docDeleteEntity`'s ELEVATION
     * case now refuses to delete the last local elevation -- but existing docs need a one-time
     * repair. `docMarshal` re-derives `optionSets.elevationStyle` from `doc.elevations` via
     * `docMergeElevationOptionSet` on every load, so this migration only needs to add the row;
     * the option-set mirror repairs itself immediately after.
     */
    const newId = uid();
    json.elevations = {
      ...json.elevations,
      [newId]: {
        id: newId,
        parentId: "elevationStyle",
        entityType: "ELEVATION",
        sortIndex: elevationIds.length,
        name: "Default elevation",
        baseSettings: "Default_01",
        defaultExteriorCamera: { x: 185, y: -940, z: 65, maxPolarAngle: 1.57 },
      },
    };

    /**
     * If a valid default already exists (doc-ref or local, doesn't matter which), leave it alone.
     * Otherwise point it at the elevation we just seeded.
     */
    const elevationStyle = doc.optionSets?.elevationStyle;
    const hasValidDefault = !!elevationStyle?.defaultValue && elevationIds.includes(elevationStyle.defaultValue);

    if (!hasValidDefault) {
      json.optionSets = {
        ...json.optionSets,
        elevationStyle: { ...elevationStyle, defaultValue: newId },
      };
    }

    return json;
  },
};

export default migration;
